import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import Button from "../common/Button";

// The "Delete Agency" tab of the staff Edit Agency dialog. A real hard
// delete: the name must be retyped, and the staff member chooses whether
// the officers' logins are deactivated (default) or the officers become
// solo accounts. Cases and audit history survive either way.
type Props = {
  agency: { id: string; name: string; officer_count?: number; subscription: { status: string } | null };
  adminEmail: string;
  onDeleted: () => void;
};

const DeleteAgencyPanel: React.FC<Props> = ({ agency, adminEmail, onDeleted }) => {
  const [typed, setTyped] = useState("");
  const [deleteOfficers, setDeleteOfficers] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTyped("");
    setDeleteOfficers(true);
  }, [agency.id]);

  const confirmed = typed.trim() === agency.name.trim();
  const officerCount = agency.officer_count ?? 0;

  const run = async () => {
    if (!confirmed) return;
    setBusy(true);
    try {
      const { data } = await axios.delete(`/api/agency-management/agencies/${agency.id}`, {
        data: { adminEmail, confirmName: typed, deleteOfficers },
        headers: { "x-admin-email": adminEmail },
      });
      const parts = [`Deleted ${agency.name}`];
      if (data.officers) parts.push(`${data.officers} officer${data.officers === 1 ? "" : "s"} ${data.officersDeactivated ? "deactivated" : "detached"}`);
      if (data.stripeCancelled?.length) parts.push(`${data.stripeCancelled.length} Stripe subscription${data.stripeCancelled.length === 1 ? "" : "s"} cancelled`);
      toast.success(parts.join(" · "));
      if (data.stripeFailed?.length) toast.error(`Could not cancel in Stripe: ${data.stripeFailed.join(", ")}. Cancel them by hand.`);
      onDeleted();
    } catch (error: any) {
      toast.error(error?.response?.data?.error || "Failed to delete agency");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-lg p-4 text-sm text-red-800 dark:text-red-300">
        <p className="m-0 font-semibold">Deleting {agency.name} cannot be undone.</p>
        <ul className="m-0 mt-2 pl-5 list-disc space-y-1">
          <li>The agency login, its admins, invitations, SSO and Axon settings, and agency-wide templates are permanently removed.</li>
          <li>Active Stripe subscriptions{agency.subscription ? ` (currently ${agency.subscription.status})` : ""} are cancelled immediately.</li>
          <li>Cases, investigations, and audit history are kept for records with the agency link cleared.</li>
        </ul>
      </div>

      {officerCount > 0 && (
        <div className="bg-base-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-gray-700 dark:text-base-content/80 mb-2">
            {officerCount} officer account{officerCount === 1 ? "" : "s"}
          </p>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="radio" name="delete-officers" className="radio radio-sm mt-0.5" checked={deleteOfficers} onChange={() => setDeleteOfficers(true)} />
            <span>
              <span className="font-medium">Deactivate their logins</span>
              <span className="block text-base-content/60">They can no longer sign in. Their cases stay in the database.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm cursor-pointer mt-2">
            <input type="radio" name="delete-officers" className="radio radio-sm mt-0.5" checked={!deleteOfficers} onChange={() => setDeleteOfficers(false)} />
            <span>
              <span className="font-medium">Keep them as solo accounts</span>
              <span className="block text-base-content/60">They keep signing in and would need their own subscription.</span>
            </span>
          </label>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
          Type <span className="font-semibold text-base-content">{agency.name}</span> to confirm
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          className="input input-bordered w-full"
          placeholder={agency.name}
          autoComplete="off"
        />
      </div>

      <div className="flex justify-end">
        <Button variant="danger" onClick={run} disabled={!confirmed || busy} loading={busy}>
          Delete agency permanently
        </Button>
      </div>
    </div>
  );
};

export default DeleteAgencyPanel;
